import React, { useEffect, useState } from "react";
import { Zap, Clock } from "lucide-react";
import api from "../lib/api";

const useCountdown = (target) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!target) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [target]);
    if (!target) return null;
    const diffSec = Math.max(Math.floor((new Date(target).getTime() - now) / 1000), 0);
    const days = Math.floor(diffSec / 86400);
    const hours = Math.floor((diffSec % 86400) / 3600);
    const mins = Math.floor((diffSec % 3600) / 60);
    if (days >= 1) return `${days}d ${hours}h left`;
    if (hours >= 1) return `${hours}h ${mins}m left`;
    return `${mins}m left`;
};

const MatchBanner = ({ donationAmount = 0, onChange }) => {
    const [match, setMatch] = useState(null);
    const countdown = useCountdown(match?.ends_at);

    useEffect(() => {
        api.get("/match/active")
            .then((r) => setMatch(r.data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (onChange) onChange(match);
    }, [match, onChange]);

    if (!match) return null;

    const bonus = Math.min(
        donationAmount * (match.multiplier - 1),
        match.remaining
    );
    const total = donationAmount + bonus;
    const poolPct = Math.min((match.matched_so_far / match.max_match_amount) * 100, 100);

    return (
        <div
            data-testid="match-banner"
            className="bg-gradient-to-br from-[#1C252E] via-[#1C252E] to-[#E53935]/40 text-white rounded-2xl p-6 mb-6 relative overflow-hidden"
        >
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-[#E53935]/30 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#E53935] flex items-center justify-center">
                            <Zap className="w-6 h-6 text-white" fill="white" strokeWidth={1.5} />
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#E53935] mb-0.5">
                                Active match · {match.multiplier}x
                            </p>
                            <p className="font-heading text-lg sm:text-xl text-white font-medium leading-tight" data-testid="match-name">
                                {match.name}
                            </p>
                            <p className="text-xs text-white/70">Sponsored by {match.sponsor}</p>
                        </div>
                    </div>
                    {countdown && (
                        <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5 text-xs font-medium" data-testid="match-countdown">
                            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {countdown}
                        </div>
                    )}
                </div>

                {donationAmount > 0 && bonus > 0 && (
                    <div className="bg-white/10 rounded-xl p-3 mt-3" data-testid="match-preview">
                        <p className="text-sm">
                            Your <strong className="text-white">${donationAmount.toFixed(0)}</strong> becomes{" "}
                            <strong className="font-heading text-2xl text-[#E53935]">${total.toFixed(0)}</strong>
                            <span className="text-white/70 text-xs ml-1">(+${bonus.toFixed(0)} match)</span>
                        </p>
                    </div>
                )}

                <div className="mt-4">
                    <div className="flex items-baseline justify-between text-xs mb-1.5">
                        <span className="text-white/70">Match pool</span>
                        <span className="text-white font-medium">
                            ${Math.round(match.matched_so_far).toLocaleString()} of ${Math.round(match.max_match_amount).toLocaleString()} unlocked
                        </span>
                    </div>
                    <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-[#E53935] rounded-full transition-all duration-700"
                            style={{ width: `${poolPct}%` }}
                            data-testid="match-pool-bar"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MatchBanner;
